import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Inject,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { PurchasePageV2FacadeService } from '../services/purchase-page-v2.facade.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    ApplicationFacade,
    CustomerAccountsFacade,
    CustomerCard,
    FavoritesFacade,
    PurchaseService
} from '@ridango/components';
import {
    ActivationMode,
    ActivationType,
    Medium,
    NextButtonElement,
    Product
} from '@ridango/components/lib/purchase/purchase.model';
import { Temporal } from '@js-temporal/polyfill';
import { BaseComponent } from '@ridango/core';
import { PurchasePageV2BaseComponent } from '../purchase-page-v2-base.component';
import { debounceTime, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { CardsPurchaseFacadeService } from '@ridango/widgets';
import { PurchaseStep } from '../models/purchase-step.model';
import Env from '@ridango/core/env';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { Platform } from '@ionic/angular';

@Component({
    templateUrl: './purchase-page-v2-wrapper.component.html',
    styleUrls: ['./purchase-page-v2-wrapper.component.scss']
})
export class PurchasePageV2WrapperComponent extends BaseComponent implements OnInit, OnDestroy {
    ActivationMode = ActivationMode;

    resizeListener: () => void;

    isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    protected readonly isLoading$ = this.purchaseService.isPurchaseDataLoading$;
    readonly isNewAfc = this.purchasePageFacade.isNewAfc;
    readonly minDate = Temporal.Now.plainDateISO().toString();

    private selectedCard = this.cardsPurchaseFacadeService.selectedCard;
    private physicalBackButtonSubscription$: Subscription;
    assetsPath$ = this.applicationFacade.assetsPath$;

    activationMode$ = this.purchaseService.activationMode$;
    selectedStartingDate$ = this.purchaseService.selectedStartingDate$;

    areas$ = this.purchasePageFacade.areas$;
    productAreas$ = this.purchasePageFacade.productAreas$;
    selectedArea$ = this.purchasePageFacade.selectedArea$;
    preferences$ = this.purchasePageFacade.preferences$;
    savedPreference$ = this.purchasePageFacade.savedPreference$;
    nextButton$ = this.purchasePageFacade.nextButton$;
    steps$ = this.purchasePageFacade.displaySteps$;
    stepFromUrl$ = this.purchasePageFacade.stepFromUrl$;
    dataFromUrl$ = this.purchasePageFacade.dataFromUrl$;
    isBackButtonHidden$ = this.purchasePageFacade.isBackButtonHidden$;
    selectedPreferenceOption$ = this.purchasePageFacade.selectedPreference$;

    isPurchaseByCard = this.cardsPurchaseFacadeService.isPurchaseByCard;

    categoryProducts = [];
    _isContrast = false;

    private get steps(): PurchaseStep[] {
        return this.purchasePageFacade.steps;
    }

    @Output() nextButtonElement = new EventEmitter<NextButtonElement>();

    @HostBinding('class.bg-grey-white_smoke') bgGreyWhiteSmoke = true;
    @HostBinding('class.bg-contrast-black') contrastBg = false;

    @ViewChild('baseComponent') baseComponent: PurchasePageV2BaseComponent;

    get activeStep(): number {
        return this.purchasePageFacade.activeStep;
    }

    get bottomPartSize(): string {
        return this.purchasePageFacade.bottomPartSize(this.isDesktop);
    }

    get shouldShowCloseButton(): boolean {
        return this.purchasePageFacade.shouldShowCloseButton();
    }

    constructor(
        @Inject('ENV') private readonly env: Env,
        private purchasePageFacade: PurchasePageV2FacadeService,
        private customerAccountsFacade: CustomerAccountsFacade,
        private cdr: ChangeDetectorRef,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private purchaseService: PurchaseService,
        private translate: TranslateService,
        private cardsPurchaseFacadeService: CardsPurchaseFacadeService,
        private favoritesFacade: FavoritesFacade,
        private location: Location,
        private platform: Platform,
        private applicationFacade: ApplicationFacade
    ) {
        super();
        this.resizeListener = () => {
            this.isDesktop = window.matchMedia('(min-width: 1024px)').matches;
            this.cdr.detectChanges();
        };
        window.addEventListener('resize', this.resizeListener);
        this.purchasePageFacade.initSteps();
    }

    ngOnInit(): void {
        this.initPurchaseFlowData();
        this.applicationFacade.highContrast$.pipe(takeUntil(this.componentDestroyed$)).subscribe((isContrast) => {
            this.contrastBg = isContrast;
            this.bgGreyWhiteSmoke = !isContrast;
            this.cdr.markForCheck?.();
        });
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.resetPurchaseFLowData();
        this.nextButtonElement.emit(null);
        window.removeEventListener('resize', this.resizeListener);
    }

    populateNextButton($event: NextButtonElement) {
        this.purchasePageFacade.setNextButton($event);
    }

    handleClosePurchasePage(): void {
        this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
    }

    handleNextButtonElement($event: NextButtonElement): void {
        this.nextButtonElement.emit($event);
        this.populateNextButton($event);
    }

    handleNextClicked(_step?: string): void {
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        this.purchasePageFacade.handleNextClicked(urlSegments, _step);
    }

    handleSelectedOptionChange(option): void {
        this.purchasePageFacade.handleSelectedOptionChange(option);
    }

    handleErrorEvent(message?: string): void {
        this.purchasePageFacade.handleErrorEvent(message);
    }

    onActivationModeChange(mode: ActivationMode): void {
        this.purchasePageFacade.onActivationModeChange(mode);
    }

    onDateChange(selectedDate: Temporal.PlainDate): void {
        this.purchasePageFacade.onDateChange(selectedDate);
    }

    handleBackClicked(): Promise<void> {
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        return this.purchasePageFacade.handleBackClicked(urlSegments);
    }

    private initDefaultPurchaseFlow(): void {
        this.updateSelectedActivationMethod();
        this.updateSelectedPreference();
        this.applyFavoritePreferenceIfPresent();
        this.handleRefreshRedirectToStart();
        this.initPurchaseFlow();
        this.setPhysicalBackButtonSubscription();
        this.checkIfAddOneMoreTicket();
        this.syncBackButtonVisibility();
        this.getAreasAccordingToSelectedAccount();
    }

    private resetPurchaseFLowData(): void {
        this.purchasePageFacade.resetData();
        this.cardsPurchaseFacadeService.reset();
        this.nextButtonElement.emit(null);
        this.physicalBackButtonSubscription$?.unsubscribe();
    }

    private initPurchaseFlowData(): void {
        this.purchaseService.setPurchaseDataLoading(true);
        this.isPurchaseByCard ? this.initPurchaseFlowFromCard() : this.initDefaultPurchaseFlow();
    }

    private initPurchaseFlowFromCard(): void {
        this.purchasePageFacade
            .getCardMedium$()
            .pipe(take(1))
            .subscribe((medium) => {
                this.initDefaultPurchaseFlow();
                this.selectPreferenceByCard(medium);
            });
    }

    private selectPreferenceByCard(medium: Medium): void {
        const card: CustomerCard = { ...this.selectedCard, id: Number(medium.id) };
        const preference = this.purchasePageFacade.createPreferenceByCard(card);

        this.handleSelectedPreferenceChange(preference);
        this.purchaseService.choosePreference(preference);
    }

    private handleSelectedPreferenceChange(option): void {
        this.purchasePageFacade.handleSelectedOptionChange(option);
    }

    private updateSelectedActivationMethod(): void {
        this.purchaseService.activationMethod$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((activationMethod) => {
                this.purchasePageFacade.setSelectedActivationMethod(activationMethod);
            });
    }

    private updateSelectedPreference(): void {
        this.purchaseService.currentPreference
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((preference) => this.purchasePageFacade.setSelectedPreferenceOption(preference));
    }

    private applyFavoritePreferenceIfPresent(): void {
        if (this.steps.some((step) => step.name.includes('preferences'))) {
            this.favoritesFacade.getFavoritePreference();
            this.favoritesFacade.favoritePreference$
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((preference) => {
                    this.purchasePageFacade.setSavedPreference(preference);
                });
        }
    }

    private handleRefreshRedirectToStart(): void {
        if (this.env.PURCHASE_FLOW?.REDIRECT_TO_START_ON_REFRESH) {
            this.redirectToStartOnRefresh();
        }
    }

    private redirectToStartOnRefresh(): void {
        this.activatedRoute.url
            .pipe(
                take(1),
                takeUntil(this.componentDestroyed$),
                filter((segments) => {
                    const productAreaAsFirstStep = segments.length === 1 && segments[0].path === 'product-area';

                    return segments.length > 1 || productAreaAsFirstStep;
                })
            )
            .subscribe(async () => {
                await this.router.navigate([this.translate.currentLang, 'purchase-v2', this.steps[0].name]);
            });
    }

    private initPurchaseFlow(): void {
        this.purchasePageFacade.initPreferences();
        this.resolvePurchaseStepFromUrl();
    }

    private resolvePurchaseStepFromUrl(): void {
        this.activatedRoute.url.pipe(takeUntil(this.componentDestroyed$)).subscribe((segments) => {
            if (segments.length === 0 || segments[0].path === '**') {
                this.router.navigate([this.translate.currentLang, 'purchase-v2', this.steps[0].name]);
            }
            const stepsFromUrl = segments
                .map((segment) => segment.path)
                .filter((path) => this.steps.some((step) => step.name === path));

            const nextStep = stepsFromUrl[stepsFromUrl.length - 1];

            // Skip ticket-activation step if here is only one possible step
            if (nextStep === 'ticket-activation') {
                this.initTicketActivationFlow(nextStep);
                return;
            }

            this.purchasePageFacade.setStepFromUrl(nextStep);

            const activeStep = this.steps.findIndex((s) => s.name === nextStep);
            this.purchasePageFacade.setActiveStep(activeStep);
            this.setDataFromUrl();
        });
    }

    private initTicketActivationFlow(nextStep: string): void {
        const selectedticketTypes = this.purchaseService
            .getStepDataIdsByStepName('product')
            .filter((item) => item.length > 2);
        // FIXME: Potential bug as if amount of people > 9 it will read that amount as product id

        const selectedProducts = selectedticketTypes.map(
            (productIdWithParameter) => productIdWithParameter.split(':')[0]
        );

        const areaId = this.purchaseService.getStepDataIdsByStepName('product-area')[0];
        const categoryProductId = this.purchaseService.getStepDataIdsByStepName('category-product')[0];
        this.purchaseService
            .getProducts([areaId].filter(Boolean))
            .pipe(take(1), takeUntil(this.componentDestroyed$))
            .subscribe((products) => {
                this.categoryProducts = [{ id: categoryProductId, products }];
                this.purchaseService.emitActivationUrl(
                    this.location.path().replace(ActivationType.AFTER_PURCHASE, 'AFTER_PURCHASE')
                );

                const filteredProducts = products.filter(function (item) {
                    return selectedProducts.indexOf(item.id) !== -1;
                });

                const activationTypes = this.generateActivationMethodsBasedOnTickets(filteredProducts);
                const uniqueActivationTypes = new Set(activationTypes);
                // Skip activation step
                if (uniqueActivationTypes.size < 2) {
                    this.purchaseService.emitSelectActivationMethod(activationTypes[0]);
                    this.handleNextClicked(nextStep);
                } else {
                    if (this.env.IS_NEW_AFC) {
                        this.purchasePageFacade.setStepFromUrl(nextStep);
                    }
                    const handleActivationStep = () => {
                        const activeStep = this.steps.findIndex((s) => s.name === this.stepFromUrl$.value);
                        this.purchasePageFacade.setActiveStep(activeStep);
                    };
                    this.productAreas$.pipe(
                        take(1),
                        tap((areas) => {
                            if (areas.length === 0) {
                                this.purchaseService
                                    .getProductAreas()
                                    .pipe(take(1), takeUntil(this.componentDestroyed$))
                                    .subscribe((productAreas) => {
                                        handleActivationStep();
                                        this.purchasePageFacade.setProductAreas(productAreas);
                                    });
                            } else {
                                handleActivationStep();
                            }
                        })
                    );
                }
            });
    }

    private generateActivationMethodsBasedOnTickets(products: Product[]): ActivationType[] {
        return products.flatMap((item) => {
            if (item.productParameters?.availableParameters?.activations) {
                return item.productParameters.availableParameters.activations.map(
                    (method) => method.activationTrigger
                ) as ActivationType[];
            }

            return item.activationMethod.map((method) => method.activationTrigger) as ActivationType[];
        });
    }

    private setDataFromUrl(): void {
        const dataFromUrl = {
            selectedPreferenceId: !this.isPurchaseByCard
                ? Number(this.purchaseService.getStepDataIdsByStepName('preferences')[0])
                : null,
            selectedAreaId: Number(this.purchaseService.getStepDataIdsByStepName('areas')[0]),
            selectedProductAreas: this.purchaseService.getStepDataIdsByStepName('product-area'),
            selectedProductId: Number(this.purchaseService.getStepDataIdsByStepName('category-product')[0]),
            ticketsFromUrl: this.purchaseService.getStepDataIdsByStepName('product')
        };

        this.purchasePageFacade.setDataFromUrl(dataFromUrl);
    }

    private setPhysicalBackButtonSubscription(): void {
        this.physicalBackButtonSubscription$ = this.platform.backButton.subscribeWithPriority(1000, () => {
            this.handleBackClicked();
        });
    }

    private checkIfAddOneMoreTicket(): void {
        this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
            const isAddOneMoreTicket = !!params['isAddOneMoreTicket'];
            this.purchasePageFacade.setIsAddOneMoreTicket(isAddOneMoreTicket);

            if (isAddOneMoreTicket) {
                // Start add-another flow from a clean staged selection.
                this.purchaseService.resetProductsLocalStorage();
                this.router.navigate([this.translate.currentLang, 'purchase-v2', this.steps[0].name], {
                    queryParams: {
                        isAddOneMoreTicket: null
                    },
                    queryParamsHandling: 'merge'
                });
            }
        });
    }

    private syncBackButtonVisibility(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.getBackButtonVisibility())
            )
            .subscribe((isHidden) => this.purchasePageFacade.setBackButtonVisibility(isHidden));
    }

    private getBackButtonVisibility(): boolean {
        const isFirsStep = this.purchasePageFacade.activeStep === 0;
        return isFirsStep && this.env.PURCHASE_FLOW?.HIDE_WALLET_LOADING_BACK_BUTTON === true;
    }

    private getAreasAccordingToSelectedAccount(): void {
        // React to account changes so the user gets areas allowed for the selected account type.
        this.customerAccountsFacade.selectedAccount$
            .pipe(
                takeUntil(this.componentDestroyed$),
                debounceTime(250),
                switchMap(() => this.purchaseService.getAreas())
            )
            .subscribe((areas) => {
                this.purchasePageFacade.setAreas(areas);
            });
    }
}
